import { ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export const cn = (...inputs: ClassValue[]) => {
  return twMerge(clsx(inputs));
};

/**
 * S3 URL의 파일명을 URL 안전한 형태로 변환하는 함수
 *
 * 동작 방식:
 * 1. 파일명이 이미 퍼센트 인코딩(%XX 형식)되어 있는지 확인
 *    - 인코딩된 경우 ("%ED%95%9C%EA%B8%80.jpg") → 원본 반환
 *    - 인코딩되지 않은 경우 ("한글.jpg") → 자모 분리 후 인코딩
 *
 * 2. URL 안전성:
 *    - 퍼센트 인코딩은 ASCII 문자만 사용 (%, 0-9, A-F)
 *    - URL에서 안전하게 사용 가능한 형식으로 변환
 *
 * 3. 처리 범위:
 *    - S3 경로([AWS s3 bucket name]/wp-content/uploads/yyyy/mm/timestamp/파일명) 중
 *    - 파일명만 처리 (상위 경로는 영문/숫자로만 구성되어 있음)
 *
 * @param src - 원본 S3 URL
 * @returns 파일명이 URL 안전한 형태로 변환된 S3 URL
 */
export function generateDecomposedS3Url(src: string): string {
  try {
    const urlParts = src.split('/');
    const fileName = urlParts.pop() as string;

    // 파일명에서 인코딩된 부분을 찾아 디코딩
    const decodedFileName = decodeURIComponent(fileName);

    // 전체 파일명을 NFD로 정규화하고 다시 인코딩
    const decomposedFileName = decodedFileName.normalize('NFD');
    const encodedFileName = encodeURIComponent(decomposedFileName);

    // URL 재조립
    urlParts.push(encodedFileName);
    return urlParts.join('/');
  } catch (error) {
    console.error('Error in generateDecomposedS3Url:', error);
    return src;
  }
}

/**
 * HTML 태그를 표준 형식으로 정규화하는 함수
 * @param content - HTML 문자열
 * @returns 정규화된 HTML 문자열
 */
export function normalizeHtmlTags(content: string): string {
  return content
    .replaceAll('<br>', '<br />')
    .replace(/<img((?!\/?>).)*?>/g, match =>
      match.endsWith('/>') ? match : match.slice(0, -1) + ' />',
    );
}

/**
 * URL을 CDN 도메인으로 변환하는 함수
 * @param content - S3 URL이 포함된 문자열
 * @returns CDN URL로 변환된 문자열
 */
export function convertToCdn(content: string): string {
  return content
    .replaceAll(
      'wp-media-homepage.s3.ap-northeast-2.amazonaws.com',
      'd15q5w6b9vqac5.cloudfront.net', //prod
    )
    .replaceAll(
      '3billion-homepage-resource.s3.ap-northeast-2.amazonaws.com',
      'd15q5w6b9vqac5.cloudfront.net', //prod
    );
}

/**
 * HTML에서 style 속성을 제거하는 함수
 * @param content - HTML 문자열
 * @returns style 속성이 제거된 HTML 문자열
 */
export function removeStyles(content: string): string {
  return content.replaceAll(/style="[^"]*"/g, '');
}

/**
 * 컨텐츠 내 이미지 URL을 처리하는 함수
 *
 * 처리 순서:
 * 1. S3 URL: 이미 인코딩되어 있으므로 그대로 반환
 * 2. YouTube URL: 인코딩하지 않고 그대로 반환
 * 3. 3billion.io URL: 디코딩 후 S3 URL로 변환하고 재인코딩
 *
 * @param content - HTML 문자열
 * @returns 처리된 URL이 포함된 HTML 문자열
 */
export function processUrls(content: string): string {
  return content.replace(/src="([^"]+)"/g, (match, url) => {
    const processedUrl = processSingleUrl(url);
    return `src="${processedUrl}"`;
  });
}

/**
 * 단일 URL에 대한 처리를 수행하는 함수
 *
 * @param url - 처리할 URL
 * @returns 처리된 URL
 */
export function processSingleUrl(url: string): string {
  // S3 URL 체크
  if (url.includes('s3.ap-northeast-2.amazonaws.com')) {
    return url;
  }

  // YouTube URL 체크
  if (url.includes('youtube.com/embed')) {
    return url;
  }

  // 3billion.io URL 처리
  if (url.includes('3billion.io')) {
    try {
      const decodedUrl = decodeURIComponent(url);
      const s3Url = decodedUrl
        .replace('www.3billion.io/img', '3billion.io/img')
        .replace(
          '3billion.io/img',
          '3billion-homepage-resource.s3.ap-northeast-2.amazonaws.com/img',
        );
      return generateDecomposedS3Url(s3Url);
    } catch (error) {
      console.error('Error processing 3billion.io URL:', error);
      return url;
    }
  }

  return url;
}

/**
 * HTML 컨텐츠에서 첫 번째 이미지 URL을 추출하고 처리하는 함수
 *
 * @param content - HTML 문자열
 * @returns 처리된 첫 번째 이미지 URL (CloudFront 도메인으로 변환됨)
 */
export function getFirstImageUrl(content: string): string {
  const match = content.match(/<img.*?src=["'](.*?)["']/);
  if (!match) return '';

  const url = match[1];
  const processedUrl = processSingleUrl(url);

  // convertToCdn 함수를 사용하여 CloudFront URL로 변환
  return convertToCdn(processedUrl);
}

/**
 * HTML 테이블에서 table 태그 바로 아래에 있는 caption 태그를 제거하는 함수
 * WordPress TablePress 등에서 생성된 편집 링크가 포함된 caption을 제거
 *
 * @param content - HTML 문자열
 * @returns caption 태그가 제거된 HTML 문자열
 */
export function removeTableCaptions(content: string): string {
  // table 태그 바로 다음에 오는 caption 제거 (속성, 공백, 줄바꿈 모두 허용)
  return content.replace(
    /(<table\b[^>]*>)(\s*)<caption\b[^>]*>[\s\S]*?<\/caption>/gi,
    '$1$2',
  );
}

/**
 * 컨텐츠 전체 처리를 위한 유틸리티 함수
 *
 * @param content - 원본 HTML 컨텐츠
 * @returns 모든 처리가 완료된 HTML 컨텐츠
 */
export function processContent(content: string | undefined): string {
  if (!content) return '';

  return removeTableCaptions(
    removeStyles(convertToCdn(processUrls(normalizeHtmlTags(content)))),
  );
}
